import {useGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {Category} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/category.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {ProductService_GetProductTileV2Lazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/product/v1/service.grpc.lazy';
import {useTrackFeature} from '@aboutyou/tracking';
import {useCallbackDelayed} from '@aboutyou/ui-components';
import {useOptionalSession} from '@aboutyou/user';
import React, {useCallback} from 'react';
import {useCategoryIdentifierCallback} from '../../../backend-services/CategoryStream/utils/useCategoryIdentifier';
import {useProtoProductImageType} from '../../../shared-deprecated/useProductImageType';
import {useReductionsStateRequestParameter} from '../../ReductionsState/useReductionsStateRequestParameter';

export const useOnColorHover = (
  initialTile: ProductTileV2,
  category?: Category,
) => {
  const [tile, setTile] = React.useState<ProductTileV2>();
  const [soldOut, setSoldOut] = React.useState<number | undefined>();
  const [loaded, setLoaded] = React.useState(false);
  const session = useOptionalSession();
  const requestTile = useGrpcRequestCallback(
    ProductService_GetProductTileV2Lazy,
  );
  const reductionsState = useReductionsStateRequestParameter();
  const trackFeature = useTrackFeature();
  const getCategoryIdentifier = useCategoryIdentifierCallback();
  const preferredImage = useProtoProductImageType();

  const onHoverHandler = useCallback(
    async (productId: number) => {
      if (productId !== initialTile.productId) {
        trackFeature({
          eventType: 'hover',
          label: 'category_page_color_picker',
        });

        const categoryIdentifier = category
          ? {
              $case: 'id' as const,
              id: category.id,
            }
          : getCategoryIdentifier();

        const fetchedTile = await requestTile({
          productId,
          session,
          reductionsState,
          category: {
            identifier:
              // This is a little hack as the legacy path case is an
              // invalid category ID for backend, at the point of implementation
              // we are not allowed to change the schema and therefore
              // backend implements custom logic to handle the 0 case
              categoryIdentifier.$case === 'legacyPath'
                ? {$case: 'id', id: 0}
                : categoryIdentifier,
          },
          productTileConfig: {
            hideBadges: false,
            hideColors: false,
            hideName: !initialTile.productName,
          },
          preferredProductImageType: preferredImage,
        });

        setTile({
          ...fetchedTile.product,
          colors: initialTile.colors,
        });
      } else {
        setTile(initialTile);
      }
    },
    [
      initialTile,
      trackFeature,
      requestTile,
      session,
      reductionsState,
      getCategoryIdentifier,
    ],
  );

  const onImageLoad = () => {
    setLoaded(true);
  };

  const {delayedCallback, cancelCallback} = useCallbackDelayed(
    onHoverHandler,
    50,
  );

  const resetTile = () => {
    cancelCallback();
    setTile(undefined);
    setSoldOut(undefined);
  };
  return {
    onHover: delayedCallback,
    resetTile,
    tile,
    onImageLoad,
    loaded,
    soldOut,
  };
};
